/**
 * @file use-custom-shipping-form-state.ts
 *
 * Controlled-form state for the custom-shipping (zone) form (no react-hook-form).
 * Holds the zone name, the selected regions, and the method list; exposes setters
 * plus method add/update/remove/reorder; tracks dirtiness against the seed; and
 * validates the name (the per-method fields are validated by their own sub-forms
 * before they enter the list). Re-seeds only when the seed reference changes, so
 * a caller passing a fresh object each render won't wipe edits — mirroring
 * `use-coupon-form-state`. The seed MUST be a stable reference (memoize it).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import type { ShippingZoneLocation } from '../../api/shop-types';
import { moveItem } from '../shipping-method-utils';
import type {
  CustomShippingFormErrors,
  CustomShippingFormFields,
  MethodFormOption,
} from './custom-shipping-form-types';

/** What {@link useCustomShippingFormState} returns to the form. */
export interface UseCustomShippingFormStateResult {
  fields: CustomShippingFormFields;
  setName: (name: string) => void;
  setLocations: (locations: ShippingZoneLocation[]) => void;
  addMethod: (option: MethodFormOption) => void;
  updateMethod: (id: string, option: MethodFormOption) => void;
  removeMethod: (id: string) => void;
  reorderMethods: (from: number, to: number) => void;
  /** Replaces the whole method list (used to apply a per-type reorder). */
  setMethods: (methods: MethodFormOption[]) => void;
  isDirty: boolean;
  errors: CustomShippingFormErrors;
  validate: () => boolean;
  /** Attach to the name field; focused when validation fails. */
  firstErrorRef: RefObject<HTMLInputElement | null>;
}

/**
 * Manages the custom-shipping form's field state and validation.
 *
 * @param seed - The initial fields (stable reference; re-seeds on change).
 *
 * @returns The fields, setters, method ops, dirty flag, errors, and `validate`.
 */
export function useCustomShippingFormState(
  seed: CustomShippingFormFields,
): UseCustomShippingFormStateResult {
  const intl = useIntl();
  const [fields, setFields] = useState<CustomShippingFormFields>(seed);
  const initialRef = useRef<CustomShippingFormFields>(seed);
  const seededRef = useRef(seed);
  const [submitted, setSubmitted] = useState(false);
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  // Re-seed only when the source reference changes (e.g. it resolves after a fetch).
  useEffect(() => {
    if (seededRef.current === seed) {
      return;
    }
    seededRef.current = seed;
    setFields(seed);
    initialRef.current = seed;
    setSubmitted(false);
  }, [seed]);

  const setName = useCallback((name: string) => {
    setFields((prev) => ({ ...prev, name }));
  }, []);

  const setLocations = useCallback((locations: ShippingZoneLocation[]) => {
    setFields((prev) => ({ ...prev, locations }));
  }, []);

  const addMethod = useCallback((option: MethodFormOption) => {
    setFields((prev) => ({ ...prev, methods: [...prev.methods, option] }));
  }, []);

  const updateMethod = useCallback((id: string, option: MethodFormOption) => {
    setFields((prev) => ({
      ...prev,
      methods: prev.methods.map((method) => (method.id === id ? option : method)),
    }));
  }, []);

  const removeMethod = useCallback((id: string) => {
    setFields((prev) => ({
      ...prev,
      methods: prev.methods.filter((method) => method.id !== id),
    }));
  }, []);

  const reorderMethods = useCallback((from: number, to: number) => {
    setFields((prev) => ({ ...prev, methods: moveItem(prev.methods, from, to) }));
  }, []);

  const setMethods = useCallback((methods: MethodFormOption[]) => {
    setFields((prev) => ({ ...prev, methods }));
  }, []);

  const computeErrors = useCallback(
    (current: CustomShippingFormFields): CustomShippingFormErrors => {
      const errors: CustomShippingFormErrors = {};
      if (current.name.trim() === '') {
        errors.nameError = intl.formatMessage(adminMessages.shippingZoneNameRequired);
      }
      return errors;
    },
    [intl],
  );

  const errors = useMemo(
    () => (submitted ? computeErrors(fields) : {}),
    [submitted, computeErrors, fields],
  );

  const validate = useCallback((): boolean => {
    setSubmitted(true);
    const result = computeErrors(fields);
    const valid = Object.keys(result).length === 0;
    if (!valid) {
      firstErrorRef.current?.focus?.();
      firstErrorRef.current?.scrollIntoView?.({ block: 'center' });
    }
    return valid;
  }, [computeErrors, fields]);

  const isDirty = useMemo(
    () => JSON.stringify(fields) !== JSON.stringify(initialRef.current),
    [fields],
  );

  return {
    fields,
    setName,
    setLocations,
    addMethod,
    updateMethod,
    removeMethod,
    reorderMethods,
    setMethods,
    isDirty,
    errors,
    validate,
    firstErrorRef,
  };
}
